import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag, StatCard } from "@/components/Layout";
import { approvalStatusTone } from "@/lib/statusTone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import usePermissions from "@/lib/usePermissions";
import { Plus, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import LeavesCalendar from "@/components/LeavesCalendar";
import LeavesInsights from "@/components/LeavesInsights";
import LeavesExportDialog from "@/components/LeavesExportDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDate } from "@/lib/dateFormat";
import useBulkSelection from "@/hooks/useBulkSelection";

const LEAVE_TYPES = [
  { v: "general", label: "General" },
  { v: "sick", label: "Sick" },
  { v: "probation", label: "Probation" },
  { v: "short_break", label: "Short break" },
  { v: "bereavement", label: "Bereavement" },
];

function leaveTypeLabel(v) {
  const t = LEAVE_TYPES.find((x) => x.v === v);
  return t ? t.label : (v || "").replace(/_/g, " ");
}

/** Calendar days a leave consumes from the user's balance.
 *  - Multi-day full leaves: inclusive day count.
 *  - Single-day with half_day=first_half|second_half: 0.5
 *  - Missing/invalid dates: "—" (so the auto-calc input shows a friendly hint)
 */
function computeDays(form) {
  const { start_date, end_date, half_day } = form || {};
  if (!start_date || !end_date) return "—";
  const sd = new Date(start_date);
  const ed = new Date(end_date);
  if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime()) || ed < sd) return "—";
  const span = Math.round((ed - sd) / 86400000) + 1;
  if ((half_day === "first_half" || half_day === "second_half") && span === 1) return "0.5";
  return String(span);
}

function rangesOverlap(a1, a2, b1, b2) {
  if (!a1 || !a2 || !b1 || !b2) return false;
  return a1 <= b2 && b1 <= a2;
}

function leaveBarColor(remaining) {
  if (remaining <= 1) return "#dc2626";
  if (remaining <= 3) return "#F54800";
  return "#16A34A";
}
function leaveTextTone(remaining) {
  if (remaining <= 1) return "text-red-600";
  if (remaining <= 3) return "text-orange-600";
  return "text-slate-700";
}

export default function Leaves() {
  const { user } = useAuth();
  const can = usePermissions();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "general", start_date: "", end_date: "", reason: "", half_day: "no" });
  const [balance, setBalance] = useState(null);
  const [allBalances, setAllBalances] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/leaves");
    setRows(data);
  };
  const loadBalances = () => {
    api.get("/leaves/balance").then((r) => setBalance(r.data)).catch(() => {});
    if (can("leaves_edit")) {
      api.get("/leaves/balance/all").then((r) => setAllBalances(r.data)).catch(() => {});
    }
  };
  useEffect(() => {
    load();
    api.get("/leaves/balance").then((r) => setBalance(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (can("leaves_edit")) {
      api.get("/leaves/balance/all").then((r) => setAllBalances(r.data)).catch(() => {});
      api.get("/users", { params: { kind: "internal" } }).then((r) => setEmployees(r.data || [])).catch(() => {});
    }
  }, [user]);

  // Deep-link from the Home leave-balance card: /my-hr?tab=leaves&apply=1[&type=sick]
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("apply") === "1") {
      setOpen(true);
      const t = searchParams.get("type");
      if (t && LEAVE_TYPES.some((x) => x.v === t)) setForm((f) => ({ ...f, leave_type: t }));
      searchParams.delete("apply");
      searchParams.delete("type");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async () => {
    try {
      const payload = { ...form };
      if (payload.half_day === "no") delete payload.half_day;
      await api.post("/leaves", payload);
      toast.success("Leave applied");
      setOpen(false);
      setForm({ leave_type: "general", start_date: "", end_date: "", reason: "", half_day: "no" });
      load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const review = async (id, status) => {
    let body = { status };
    if (status === "rejected") {
      const reason = window.prompt("Reason for rejecting this leave request? (required — included in the email to the employee)");
      if (!reason || !reason.trim()) { toast.error("Rejection cancelled — a reason is required"); return; }
      body.decision_reason = reason.trim();
    }
    try { await api.patch(`/leaves/${id}`, body); load(); toast.success(`Leave ${status} · email sent`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const canReview = can("leaves_edit");
  const isAdmin = canReview;
  // Match backend today_str_ist() (Asia/Kolkata calendar day) — a plain UTC
  // ISO date would be a day behind between 00:00-05:29 IST and could show a
  // Withdraw button the API then rejects with 400.
  const todayIso = useMemo(() => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }), []);

  const canWithdraw = (r) =>
    (r.status === "pending" || r.status === "approved") &&
    r.start_date >= todayIso &&
    (r.user_id === user?.id || isAdmin);

  const confirmWithdraw = async () => {
    if (!withdrawTarget) return;
    setWithdrawing(true);
    try {
      await api.post(`/leaves/${withdrawTarget.id}/withdraw`);
      toast.success(
        withdrawTarget.status === "approved"
          ? `Leave withdrawn — ${withdrawTarget.days} day(s) credited back to ${withdrawTarget.user_id === user?.id ? "your" : `${withdrawTarget.user_name}'s`} balance`
          : "Leave request withdrawn"
      );
      setWithdrawTarget(null);
      load();
      loadBalances();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWithdrawing(false); }
  };

  // Summaries by type
  const summary = useMemo(() => {
    const out = { total: rows.length, pending: 0, approved: 0, rejected: 0, withdrawn: 0, byType: {} };
    LEAVE_TYPES.forEach((t) => { out.byType[t.v] = 0; });
    for (const r of rows) {
      if (r.status === "pending") out.pending++;
      else if (r.status === "approved") out.approved++;
      else if (r.status === "rejected") out.rejected++;
      else if (r.status === "withdrawn") out.withdrawn++;
      if (out.byType[r.leave_type] !== undefined) out.byType[r.leave_type]++;
    }
    return out;
  }, [rows]);

  // Compute conflict map (only meaningful for admin reviewers seeing whole team)
  const conflictMap = useMemo(() => {
    const approved = rows.filter((r) => r.status === "approved");
    const map = {}; // leave id -> array of conflicting names
    for (const r of rows) {
      if (r.status !== "pending") continue;
      const others = approved.filter((a) => a.user_id !== r.user_id && rangesOverlap(r.start_date, r.end_date, a.start_date, a.end_date));
      if (others.length) map[r.id] = others.map((o) => o.user_name);
    }
    return map;
  }, [rows]);

  const displayedRows = useMemo(() => {
    if (employeeFilter === "all") return rows;
    return rows.filter((r) => r.user_id === employeeFilter);
  }, [rows, employeeFilter]);

  const bulk = useBulkSelection({
    collection: "leaves",
    ids: displayedRows.map((r) => r.id),
    onDone: load,
  });

  const displayBalance = useMemo(() => {
    if (employeeFilter === "all") return balance;
    const u = allBalances?.users?.find((x) => x.user_id === employeeFilter);
    return u ? { year: allBalances.year, balances: u.balances } : null;
  }, [employeeFilter, balance, allBalances]);

  const requestsPanel = (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Pending" value={summary.pending} accent="text-orange-700" />
        <StatCard label="Approved" value={summary.approved} accent="text-green-700" />
        <StatCard label="Rejected" value={summary.rejected} accent="text-red-700" />
        <StatCard label="Withdrawn" value={summary.withdrawn} accent="text-slate-500" />
        <StatCard label="Total" value={summary.total} />
      </div>

      {/* Phase 64 — team-wide calendar with holidays + everyone's approved leaves */}
      <LeavesCalendar />

      {isAdmin && (
        <div className="flex items-center gap-2 mb-3">
          <Label className="label-overline shrink-0">Balances for</Label>
          <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-56" data-testid="leaves-employee-filter"><SelectValue placeholder="Myself" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Myself · everyone's requests</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {LEAVE_TYPES.map((t) => {
          const bal = displayBalance?.balances?.find((b) => b.leave_type === t.v);
          return (
            <div key={t.v} className={`card-flat p-4 ${bal?.adjusted ? "ring-1 ring-amber-300 bg-amber-50/40" : ""}`} data-testid={`leave-type-${t.v}`}>
              <div className="label-overline flex items-center justify-between gap-1">
                <span>{t.label}</span>
                {bal?.adjusted && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm normal-case tracking-normal"
                    title="Includes a manual founder adjustment"
                    data-testid={`leave-type-adjusted-badge-${t.v}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" /> Adjusted
                  </span>
                )}
              </div>
              {bal ? (
                <>
                  <div className="mt-1 font-display text-2xl font-bold text-slate-900">
                    {bal.taken}<span className="text-slate-400 text-lg">/{bal.entitled}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">{bal.remaining} days remaining</div>
                  <div className="h-1 bg-slate-100 rounded-sm overflow-hidden mt-2">
                    <div className="h-full" style={{ width: `${Math.min(100, (bal.taken / Math.max(1, bal.entitled)) * 100)}%`, background: leaveBarColor(bal.remaining) }} />
                  </div>
                </>
              ) : (
                <div className="mt-1 font-display text-2xl font-bold text-slate-900">{employeeFilter === "all" ? (summary.byType[t.v] || 0) : 0}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 label-overline">
              {bulk.canBulk && <div className="w-5 shrink-0"><bulk.HeaderCheckbox /></div>}
              <div className="grid grid-cols-12 gap-4 flex-1">
                <div className="col-span-2">Employee</div>
                <div className="col-span-2">Type</div>
                <div>Start</div>
                <div>End</div>
                <div className="col-span-3">Reason</div>
                <div className="col-span-3 text-right">Status / Actions</div>
              </div>
            </div>
            <div className="divide-row">
              {displayedRows.map((r) => {
            const conflicts = conflictMap[r.id];
            const hasConflict = conflicts && conflicts.length > 0;
            const conflictBlocksNonAdmin = hasConflict && !isAdmin;
            const showAdminConflictApprove = r.status === "pending" && hasConflict && isAdmin;
            const showStandardApprove = r.status === "pending" && !hasConflict && canReview;
            return (
              <div key={r.id} className={`flex items-center gap-3 px-5 py-3 text-sm hover-row ${bulk.isSelected(r.id) ? "bg-orange-50/40" : ""}`} data-testid={`leave-row-${r.id}`}>
                {bulk.canBulk && <div className="w-5 shrink-0"><bulk.RowCheckbox id={r.id} /></div>}
                <div className="grid grid-cols-12 gap-4 items-center flex-1">
                <div className="col-span-2 text-slate-800">{r.user_name}</div>
                <div className="col-span-2 capitalize">
                  <Tag tone="slate">{leaveTypeLabel(r.leave_type)}</Tag>
                </div>
                <div>{formatDate(r.start_date)}</div>
                <div>{formatDate(r.end_date)}</div>
                <div className="col-span-3 truncate text-slate-600">
                  {r.reason || "—"}
                  {hasConflict && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded-sm" data-testid={`leave-conflict-${r.id}`}>
                      <AlertTriangle size={11} /> Overlaps with {conflicts.join(", ")}
                    </div>
                  )}
                  {r.status === "withdrawn" && r.withdrawn_by_name && (
                    <div className="mt-1 text-[11px] text-slate-400" data-testid={`leave-withdrawn-hint-${r.id}`}>
                      Withdrawn by {r.withdrawn_by_name === r.user_name ? "self" : r.withdrawn_by_name}
                      {r.withdrawn_at ? ` on ${formatDate(r.withdrawn_at)}` : ""}
                    </div>
                  )}
                </div>
                <div className="col-span-3 text-right flex items-center justify-end gap-2 flex-wrap">
                  <Tag tone={approvalStatusTone(r.status)}>{r.status}</Tag>
                  {r.status === "pending" && conflictBlocksNonAdmin && (
                    <span className="text-[11px] text-slate-500 italic">Admin approval required (conflict)</span>
                  )}
                  {r.status === "pending" && (showAdminConflictApprove || showStandardApprove) && (
                    <span className="text-[10px] text-slate-400 italic">Decide on Approvals page</span>
                  )}
                  {canWithdraw(r) && (
                    <Button
                      variant="outline" size="sm"
                      className="rounded-sm h-7 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={() => setWithdrawTarget(r)}
                      data-testid={`withdraw-leave-btn-${r.id}`}
                    >
                      Withdraw
                    </Button>
                  )}
                </div>
                </div>
              </div>
            );
          })}
          {displayedRows.length === 0 && <div className="p-5 text-sm text-slate-500">{employeeFilter === "all" ? "No leave requests yet." : "No leave requests for this employee."}</div>}
            </div>
          </div>
        </div>
      </div>
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
    </>
  );

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="leaves-page">
      <PageHeader
        eyebrow="Time off"
        title="Leaves"
        subtitle="Apply for a day off or review pending requests. Same-day overlaps with approved leaves are flagged for admin prioritisation."
        actions={
          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
            {isAdmin && <LeavesExportDialog />}
            <BulkUploadDialog section="leaves" label="Leaves" onCommitted={load} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="apply-leave-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"><Plus size={14} className="mr-1.5" />Apply leave</Button></DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-display">Apply for leave</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="label-overline">Type</Label>
                  <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEAVE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="label-overline">Start</Label><Input type="date" className="rounded-sm mt-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" /></div>
                  <div><Label className="label-overline">End</Label><Input type="date" className="rounded-sm mt-1" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-overline">Half Day?</Label>
                    <Select value={form.half_day} onValueChange={(v) => setForm({ ...form, half_day: v })}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="leave-half-day"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No — Full Day</SelectItem>
                        <SelectItem value="first_half">Yes — First Half</SelectItem>
                        <SelectItem value="second_half">Yes — Second Half</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-overline">Number of days</Label>
                    <Input
                      readOnly
                      className="rounded-sm mt-1 bg-slate-50 font-mono text-sm text-slate-700"
                      value={computeDays(form)}
                      data-testid="leave-days-calc"
                    />
                  </div>
                </div>
                <div><Label className="label-overline">Reason</Label><Textarea rows={3} className="rounded-sm mt-1" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason" /></div>
              </div>
              <DialogFooter><Button onClick={apply} data-testid="submit-leave" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Submit</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {isAdmin ? (
        <Tabs defaultValue="requests" className="w-full">
          <TabsList className="rounded-sm mb-4">
            <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-sm">Requests</TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights" className="rounded-sm">Insights</TabsTrigger>
          </TabsList>
          <TabsContent value="requests" className="mt-0">{requestsPanel}</TabsContent>
          <TabsContent value="insights" className="mt-0"><LeavesInsights allLeaves={rows} isFounder={user?.role === "founder"} /></TabsContent>
        </Tabs>
      ) : requestsPanel}

      <AlertDialog open={!!withdrawTarget} onOpenChange={(o) => !o && setWithdrawTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="withdraw-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this leave request?</AlertDialogTitle>
            <AlertDialogDescription>
              {withdrawTarget && (
                <>
                  {leaveTypeLabel(withdrawTarget.leave_type)} · {formatDate(withdrawTarget.start_date)}
                  {withdrawTarget.start_date !== withdrawTarget.end_date ? ` → ${formatDate(withdrawTarget.end_date)}` : ""}
                  {" "}({withdrawTarget.days} day{withdrawTarget.days === 1 ? "" : "s"})
                  <br />
                  {withdrawTarget.status === "approved"
                    ? "This leave is already approved — withdrawing will credit the days back to the balance."
                    : "This pending request will be marked withdrawn and removed from the approval queue."}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="withdraw-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmWithdraw}
              disabled={withdrawing}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              data-testid="withdraw-confirm-btn"
            >
              {withdrawing ? "Withdrawing…" : "Withdraw"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
